import React from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Box,
    useTheme
} from '@mui/material';
import {
    MoreVert as MoreVertIcon,
    Close as CloseIcon,
    AspectRatio as ResizeIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';

export type WidgetSize = 'small' | 'medium' | 'large';

export interface BaseWidgetProps {
    title: string;
    size: WidgetSize;
    onRemove?: () => void;
    onResize?: (newSize: WidgetSize) => void;
    isEditMode?: boolean;
    children: React.ReactNode;
    icon?: React.ReactNode;
    dragHandleProps?: any;
    /** Sizes offered in edit mode. A single entry locks the widget at that size. */
    allowedSizes?: WidgetSize[];
}

const BaseWidget: React.FC<BaseWidgetProps> = ({
    title,
    size,
    onRemove,
    onResize,
    isEditMode = false,
    children,
    icon,
    dragHandleProps,
    allowedSizes
}) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    // Design System: Nautilus Console
    const consoleColors = {
        deepOcean: '#0f2134',
        sand: '#ccba9b',
        border: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 33, 52, 0.12)',
        headerBg: theme.palette.mode === 'dark' ? 'rgba(15, 33, 52, 0.4)' : 'rgba(15, 33, 52, 0.02)',
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleResize = (newSize: WidgetSize) => {
        if (onResize) {
            onResize(newSize);
        }
        handleMenuClose();
    };

    const sizeOptions: { size: WidgetSize; label: string }[] = [
        { size: 'small', label: 'Small (1/3)' },
        { size: 'medium', label: 'Medium (2/3)' },
        { size: 'large', label: 'Large (Full)' },
    ];
    const visibleSizes = allowedSizes
        ? sizeOptions.filter((option) => allowedSizes.includes(option.size))
        : sizeOptions;

    return (
        <Card
            elevation={0} // PANEL Design - 0 Elevation, Border only
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                border: isEditMode
                    ? `1px dashed ${theme.palette.primary.main}`
                    : `1px solid ${consoleColors.border}`,
                borderRadius: 2, // Slightly more rounded for modern feel, or 1 for strict technical
                backgroundColor: theme.palette.background.paper,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    // Subtle lift, not dramatic
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(15, 33, 52, 0.25)',
                }
            }}
        >
            {(title || isEditMode) && (
                <CardHeader
                    avatar={icon ? (
                        <Box sx={{
                            color: theme.palette.mode === 'dark' ? consoleColors.sand : 'primary.main',
                            display: 'flex',
                            opacity: 0.9
                        }}>
                            {icon}
                        </Box>
                    ) : null}
                    action={
                        isEditMode && (
                            <>
                                <IconButton onClick={handleMenuClick} size="small" sx={{ color: 'text.secondary' }}>
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleMenuClose}
                                >
                                    {visibleSizes.length > 1 && visibleSizes.map((option) => (
                                        <MenuItem
                                            key={option.size}
                                            onClick={() => handleResize(option.size)}
                                            selected={size === option.size}
                                        >
                                            <ListItemIcon><ResizeIcon fontSize="small" /></ListItemIcon>
                                            <ListItemText>{option.label}</ListItemText>
                                        </MenuItem>
                                    ))}
                                    <MenuItem onClick={onRemove} sx={{ color: 'error.main' }}>
                                        <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                                        <ListItemText>Remove</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </>
                        )
                    }
                    title={
                        title && (
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.02em',
                                    textTransform: 'uppercase', // Technical Header
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {title}
                            </Typography>
                        )
                    }
                    sx={{
                        py: 1.5,
                        px: 2,
                        borderBottom: `1px solid ${consoleColors.border}`,
                        backgroundColor: consoleColors.headerBg,
                    }}
                    {...dragHandleProps}
                />
            )}
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2, overflow: 'auto' }}>
                {children}
            </CardContent>
        </Card>
    );
};

export default BaseWidget;
